import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Scissors,
  LayoutDashboard,
  Calendar,
  Users,
  UserCircle,
  Wrench,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Settings,
  Image
} from "lucide-react";

const BRAND_NAME = "Salon Bahadır Kersü";

// Admin Sidebar Component
export const AdminSidebar = ({ mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const links = [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/appointments", icon: Calendar, label: "Randevular" },
    { to: "/admin/calendar", icon: Calendar, label: "Takvim" },
    { to: "/admin/customers", icon: Users, label: "Müşteriler" },
    { to: "/admin/barbers", icon: UserCircle, label: "Berberler" },
    { to: "/admin/services", icon: Wrench, label: "Hizmetler" },
    { to: "/admin/reviews", icon: MessageSquare, label: "Yorumlar" },
    { to: "/admin/working-hours", icon: Clock, label: "Çalışma Saatleri" },
    { to: "/admin/portfolio", icon: Image, label: "Referanslar" },
    { to: "/admin/settings", icon: Settings, label: "Ayarlar" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside className={`admin-sidebar z-50 transform transition-transform lg:transform-none ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-6 border-b border-[#e5e7eb]">
          <Link to="/" className="flex items-center gap-2">
            <Scissors className="w-6 h-6 text-[#c9a96e]" />
            <span className="font-display text-lg font-semibold text-[#1a1a2e]">{BRAND_NAME}</span>
          </Link>
          <p className="text-xs text-[#c9a96e] font-medium mt-1">Yönetici Paneli</p>
        </div>

        <nav className="p-3 space-y-0.5">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`admin-sidebar-link ${isActive(link.to) ? "active" : ""}`}
              onClick={() => setMobileOpen(false)}
            >
              <link.icon className="w-5 h-5" />
              <span className="text-sm">{link.label}</span>
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#e5e7eb]">
          <button
            onClick={handleLogout}
            className="admin-sidebar-link w-full text-[#dc2626] hover:bg-red-50"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Çıkış Yap</span>
          </button>
        </div>
      </aside>
    </>
  );
};

// Admin Layout Component
export const AdminLayout = ({ children, title }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <AdminSidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-[#e5e7eb] px-4 py-3 z-30">
        <div className="flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} className="p-1">
            <Menu className="w-5 h-5 text-[#1a1a2e]" />
          </button>
          <span className="font-display text-base text-[#1a1a2e]">{title}</span>
          <div className="w-6"></div>
        </div>
      </div>

      <main className="admin-content pt-14 lg:pt-0 px-3 md:px-6 lg:px-8">
        {title && (
          <div className="hidden lg:flex items-center justify-between mb-8">
            <h1 className="font-display text-2xl text-[#1a1a2e]">{title}</h1>
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

// Admin Dashboard Page
export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, appointmentsRes] = await Promise.all([
          axios.get(`${API}/admin/dashboard`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/appointments?status=pending`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setStats(statsRes.data);
        setRecentAppointments(appointmentsRes.data.slice(0, 5));
      } catch (error) {
        toast.error("Veriler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleApprove = async (id) => {
    try {
      await axios.put(
        `${API}/appointments/${id}`,
        { status: "approved" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Randevu onaylandı");
      setRecentAppointments(prev => prev.filter(a => a.id !== id));
      setStats(prev => prev ? { ...prev, pending_appointments: prev.pending_appointments - 1 } : null);
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(
        `${API}/appointments/${id}`,
        { status: "rejected" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Randevu reddedildi");
      setRecentAppointments(prev => prev.filter(a => a.id !== id));
      setStats(prev => prev ? { ...prev, pending_appointments: prev.pending_appointments - 1 } : null);
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      ) : (
        <div>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="stat-card">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#f5efe6] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#c9a96e]" />
                </div>
              </div>
              <p className="stat-value">{stats?.today_appointments || 0}</p>
              <p className="stat-label">Bugünkü Randevular</p>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
              </div>
              <p className="stat-value">{stats?.pending_appointments || 0}</p>
              <p className="stat-label">Onay Bekleyen</p>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="stat-value">{stats?.completed_today || 0}</p>
              <p className="stat-label">Bugün Tamamlanan</p>
            </div>

            <div className="stat-card">
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <p className="stat-value">{stats?.total_customers || 0}</p>
              <p className="stat-label">Toplam Müşteri</p>
            </div>
          </div>

          {/* Revenue Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="stat-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#f5efe6] flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#c9a96e]" />
                </div>
                <div>
                  <p className="stat-label">Bugünkü Gelir</p>
                  <p className="stat-value">{stats?.total_revenue_today || 0}₺</p>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="stat-label">Bu Ayki Gelir</p>
                  <p className="stat-value">{stats?.total_revenue_month || 0}₺</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Appointments */}
          <div className="bg-white border border-[#e5e7eb] rounded-xl p-6">
            <h2 className="font-display text-xl text-[#1a1a2e] mb-4 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Onay Bekleyen Randevular
            </h2>

            {recentAppointments.length === 0 ? (
              <p className="text-[#6b7280] text-center py-8">Onay bekleyen randevu yok</p>
            ) : (
              <div className="space-y-3">
                {recentAppointments.map((appointment) => (
                  <div
                    key={appointment.id}
                    className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 bg-[#fafafa] border border-[#f3f4f6] rounded-lg"
                  >
                    <div>
                      <p className="text-[#1a1a2e] font-semibold">
                        {appointment.customer_name || appointment.customer_phone}
                      </p>
                      <p className="text-[#6b7280] text-sm">
                        {appointment.service_name} - {appointment.barber_name}
                      </p>
                      <p className="text-[#9ca3af] text-sm">
                        {appointment.date} {appointment.time}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleApprove(appointment.id)}
                        className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                      >
                        Onayla
                      </button>
                      <button
                        onClick={() => handleReject(appointment.id)}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                      >
                        Reddet
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Link to="/admin/appointments" className="block mt-4 text-center">
              <span className="text-[#c9a96e] text-sm font-medium hover:underline">Tüm randevuları görüntüle</span>
            </Link>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
